<template>
  <div :class="cn(containerVariants({ type, size, isScrollable }))">
    <slot name="leading" />
    <div
      v-for="(item, index) in itemsWithLimit"
      :key="index"
      :class="[
        isScrollable ? '!min-w-fit' : 'truncate',
        item.collapsed ? 'min-w-auto' : 'min-w-[40px]',
        index === 0 || isFirstCollapsed(index) ? 'shrink-0' : 'shrink',
      ]"
      class="flex items-center"
    >
      <div
        v-if="!item.collapsed || isFirstCollapsed(index)"
        class="flex items-center shrink truncate"
      >
        <Tooltip
          :class="isScrollable ? '' : 'truncate'"
          :title="getTooltipContent(index)"
          sticky="partial"
          size="2xl"
          side="bottom"
          disableHoverableContent
          ignoreNonKeyboardFocus
          useCustomSlot
          class="flex items-center min-w-full cursor-default"
          triggerAs="span"
          :disabled="isScrollable"
        >
          <Button
            v-if="!item.collapsed"
            :variant="buttonVariant"
            size="xs"
            :color="buttonType"
            @click="clickHandler(item)"
            :class="
              cn(
                itemVariants({
                  type,
                  size,
                  active: index === itemsWithLimit.length - 1,
                })
              )
            "
            >{{ item.label }}</Button
          >
          <span
            v-else
            :class="`breadcrumb-fg select-none text-${
              size === 'sm' ? 'xs' : 'sm'
            }`"
          >
            ...
          </span>
          <Icon
            v-if="index !== itemsWithLimit.length - 1"
            name="chevron-right"
            :class="cn(dividerVariants({ type, size }))"
          />
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from "@/SharedComponents/Icon";
import { Button } from "@/SharedComponents/Buttons";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { computed } from "vue";
import {
  types,
  sizes,
  containerVariants,
  itemVariants,
  dividerVariants,
} from ".";
import { cn } from "@/Utils/cn";

const emit = defineEmits(["clickItem"]);
const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => sizes.includes(value),
  },
  type: {
    type: String,
    default: "text",
    validator: (value) => types.includes(value),
  },
  limit: {
    type: [String, Number],
    default: 0,
  },
  isScrollable: {
    type: Boolean,
    default: false,
  },
});

const itemsWithLimit = computed(() => {
  if (props.limit === 0) return props.items;
  return props.items.map((item, index) => {
    const collapsed = shouldShowTooltip(index);
    return {
      ...item,
      collapsed,
    };
  });
});

const buttonVariant = computed(() => {
  switch (props.type) {
    case "button-brand":
      return "brand";
    case "button-gray":
      return "gray";
    default:
      return "link";
  }
});

const buttonType = computed(() => {
  switch (props.type) {
    case "button-brand":
      return "default";
    case "button-gray":
      return "gray";
    default:
      return "default";
  }
});

const shouldShowTooltip = (index) => {
  return props.limit !== 0 && index !== 0 && index !== props.items.length - 1;
};

const getTooltipContent = (index) => {
  if (!shouldShowTooltip(index)) return props.items[index].label;

  const hiddenItems = props.items.slice(1, -1);
  return hiddenItems.map((item) => item.label).join(" > ");
};

const clickHandler = (item) => {
  emit("clickItem", item);
};

const isFirstCollapsed = (index) => {
  return itemsWithLimit.value.findIndex((item) => item.collapsed) === index;
};
</script>
